import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { fromEvent, Observable, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  shareReplay,
  takeUntil,
} from 'rxjs/operators';

@Component({
  selector: 'app-detail-vehicle',
  templateUrl: './detail-vehicle.component.html',
  styleUrls: ['./detail-vehicle.component.scss'],
})
export class DetailVehicleComponent implements OnDestroy {
  @Input() value = '';
  @Input() label = '';
  @Input() variant: 'default' | 'edit' = 'default';
  @Output() updateNameUnit = new EventEmitter<string>();
  @ViewChild('inputEdit') inputEdit!: ElementRef<HTMLInputElement>;
  public isCopy = false;
  public isEdit = false;
  private currentInputSubscription?: Subject<boolean>;
  private readonly _errorSubject = new Subject<{
    isError: boolean;
    errorMessage: string;
  }>();
  readonly errorState$ = new Observable<{
    isError: boolean;
    errorMessage: string;
  }>();
  private onDestroy$ = new Subject<boolean>();

  constructor(private cdr: ChangeDetectorRef) {
    this._errorSubject.next({ isError: false, errorMessage: '' });
    this.errorState$ = this._errorSubject
      .asObservable()
      .pipe(
        takeUntil(this.onDestroy$),
        shareReplay({ bufferSize: 1, refCount: true })
      );
  }

  private updateError(error: { isError: boolean; errorMessage: string }) {
    this._errorSubject.next(error);
  }

  copyValue(event: Event): void {
    event.stopPropagation();
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(this.value)
        .then(() => {
          this.enableCopy();
          setTimeout(() => {
            this.disableCopy();
          }, 500);
        })
        .catch((err) => {
          console.error('Error al copiar al portapapeles: ', err);
        });
    } else {
      console.warn('El portapapeles no está soportado en este navegador');
    }
  }

  hideTooltip(): void {
    this.disableCopy();
  }

  editValue(event: Event): void {
    event.stopPropagation();
    this.enableEdit();
    this.inputEdit.nativeElement.focus();
  }

  cancelEdit(event: Event): void {
    event.stopPropagation();
    this.disableEdit();
  }

  acceptEdit(event: Event): void {
    event.stopPropagation();
    const name = this.inputEdit.nativeElement.value.trim();
    if (!this.isValidName(name)) {
      const error = this.validateNameAndEmitError(name);
      this.updateError(error);
      this.setupInputValidation();
      this.cdr.detectChanges();
      return;
    }
    const newName = this.sanitizeName(name);
    this.updateNameUnit.emit(newName);
    this.disableEdit();
  }

  private sanitizeName(name: string): string {
    if (!name) return this.value;
    return (
      name
        .trim()
        // Elimina caracteres especiales excepto guiones y espacios
        // .replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s-]/g, '')
        .replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s()-]/g, '')
        // Reemplaza múltiples espacios con uno solo
        .replace(/\s+/g, ' ')
        // Reemplaza múltiples guiones con uno solo
        .replace(/-+/g, '-')
        // Capitaliza primera letra de cada palabra
        .replace(/\b\w/g, (char) => char.toUpperCase())
      // // Limita la longitud máxima (opcional)
      // .slice(0, 20)
    );
  }

  private isValidName(name: string): boolean {
    if (name === undefined || name === null) {
      return false;
    }
    if (name.length === 0) {
      return false;
    }
    if (name === this.value) {
      return false;
    }
    // if (name.length > 20) {
    //   return false;
    // }
    return true;
  }

  private setupInputValidation(): void {
    this.cleanInputValidation();
    this.currentInputSubscription = new Subject<boolean>();
    fromEvent(this.inputEdit.nativeElement, 'input')
      .pipe(
        takeUntil(this.currentInputSubscription),
        takeUntil(this.onDestroy$),
        shareReplay({ bufferSize: 1, refCount: true }),
        map((e: Event) => {
          const value = (e.target as HTMLInputElement).value.trim();
          return value;
        }),
        distinctUntilChanged(),
        map((value: string) => {
          return this.validateNameAndEmitError(value);
        })
      )
      .subscribe((error) => {
        this.updateError(error);
        this.cdr.detectChanges();
      });
  }

  private cleanInputValidation(): void {
    if (this.currentInputSubscription) {
      this.currentInputSubscription.next(true);
      this.currentInputSubscription.complete();
      this.currentInputSubscription = undefined;
    }
  }

  private validateNameAndEmitError(name: string): {
    isError: boolean;
    errorMessage: string;
  } {
    if (name === undefined || name === null) {
      return { isError: true, errorMessage: 'Nombre no válido' };
    }
    if (name.length === 0) {
      return { isError: true, errorMessage: 'Nombre no puede estar vacío' };
    }
    if (name === this.value) {
      return { isError: true, errorMessage: 'Nombre no puede ser el mismo' };
    }
    // if (name.length > 20) {
    //   return { isError: true, errorMessage: 'Nombre demasiado largo' };
    // }

    return { isError: false, errorMessage: '' };
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  private disableEdit(): void {
    this.isEdit = false;
    this._errorSubject.next({ isError: false, errorMessage: '' });
    this.cleanInputValidation();
    this.cdr.detectChanges();
  }

  private enableEdit(): void {
    this.isEdit = true;
    this.cdr.detectChanges();
  }

  private disableCopy(): void {
    this.isCopy = false;
    this.cdr.detectChanges();
  }

  private enableCopy(): void {
    this.isCopy = true;
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
    this._errorSubject.complete();
  }
}
